import React, { useCallback, useState, useContext, useEffect } from 'react';
import {
  Typography, Upload, Button, Alert, Descriptions, Tag, Space, Spin,
  message, Table, Select, Popconfirm, Segmented, DatePicker, Input,
} from 'antd';
import {
  InboxOutlined, UploadOutlined, SaveOutlined,
  CloudUploadOutlined, TableOutlined, IdcardOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext, hasGrant } from '../App';

const { Dragger } = Upload;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function CNEDailyStatsUploadPage() {
  const currentUser = useContext(UserContext);
  // Role, or an explicit grant for this page. The grant is additive, so it
  // opens the page for someone outside the CNE team and never closes it.
  const isAuthorized = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin'
    || (currentUser?.JobID === 6 && currentUser?.TeamID === 3)
    || hasGrant(currentUser, 'cne-daily-upload');

  const [view, setView]         = useState('upload');

  // The AWS ID mapping tab. This table stores no UserID -- every read joins on
  // AWSID -- so mapping an agent here links every day they have ever appeared
  // on, with nothing to re-upload.
  const [teamUsers,      setTeamUsers]      = useState([]);
  const [unmapped,       setUnmapped]       = useState([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [awsIdDraft,     setAwsIdDraft]     = useState({});
  const [savingIds,      setSavingIds]      = useState(new Set());
  const [file, setFile]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [preview, setPreview]   = useState(null);
  const [error, setError]       = useState(null);
  const [saving, setSaving]     = useState(false);
  const [result, setResult]     = useState(null);

  // AWSID -> campaign name. Keyed on the agent, never on a row index: the
  // table is sortable and the key has to survive a reorder.
  const [assignments, setAssignments] = useState({});

  const [range, setRange]       = useState([dayjs().subtract(1, 'month'), dayjs()]);
  const [uploaded, setUploaded] = useState([]);
  const [upLoading, setUpLoading] = useState(false);

  useEffect(() => {
    if (!isAuthorized || view !== 'browse' || !range?.[0] || !range?.[1]) return;
    setUpLoading(true);
    api.get('/api/admin/cne-daily/uploaded', {
      params: {
        date_from: range[0].format('YYYY-MM-DD'),
        date_to:   range[1].format('YYYY-MM-DD'),
      },
    })
      .then((r) => setUploaded(r.data || []))
      .catch(() => setUploaded([]))
      .finally(() => setUpLoading(false));
  }, [isAuthorized, view, range]);


  const reset = () => { setPreview(null); setAssignments({}); setError(null); setResult(null); };

  const handlePreview = async () => {
    if (!file) { message.warning('Select a file first.'); return; }
    setLoading(true); reset();
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api.post('/api/admin/cne-daily/preview', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      const seed = {};
      (res.data.agents || []).forEach((a) => { seed[a.AWSID] = a.SuggestedCampaign; });
      setAssignments(seed);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not read the file.');
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    setSaving(true);
    const form = new FormData();
    form.append('file', file);
    form.append('assignments', JSON.stringify(assignments));
    try {
      const res = await api.post('/api/admin/cne-daily/commit', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setPreview(null);
      setFile(null);
      setAssignments({});
      message.success('Upload saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const campaignOptions = (preview?.campaigns || []).map((c) => ({ label: c.Name, value: c.Name }));
  const changedCount = (preview?.agents || [])
    .filter((a) => assignments[a.AWSID] && assignments[a.AWSID] !== a.SuggestedCampaign).length;

  const agentColumns = [
    {
      title: 'Agent', key: 'agent', width: 260,
      sorter: (a, b) => (a.LastName || '').localeCompare(b.LastName || ''),
      render: (_, a) => (
        <div>
          <div>{a.DisplayName || `${a.FirstName} ${a.LastName}`.trim() || a.AWSID}</div>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>
            {a.UserID
              ? <>QAUser #{a.UserID}{a.JobID != null && <> · Job {a.JobID}</>}{a.IsActive === false && <> · <Tag color="default" style={{ marginLeft: 2 }}>Inactive</Tag></>}</>
              : <Tag color="orange">No AWS ID on file</Tag>}
          </div>
        </div>
      ),
    },
    {
      title: 'Campaign', key: 'campaign', width: 200,
      render: (_, a) => (
        <div>
          <Select
            size="small"
            style={{ width: 170 }}
            value={assignments[a.AWSID]}
            options={campaignOptions}
            onChange={(v) => setAssignments((p) => ({ ...p, [a.AWSID]: v }))}
          />
          {assignments[a.AWSID] !== a.SuggestedCampaign && (
            <div style={{ fontSize: 11, color: '#fa8c16' }}>changed from {a.SuggestedCampaign}</div>
          )}
        </div>
      ),
    },
    { title: 'Days', dataIndex: 'Days', key: 'Days', width: 70, align: 'right',
      sorter: (a, b) => a.Days - b.Days },
    { title: 'First Day', dataIndex: 'FirstDate', key: 'FirstDate', width: 110 },
    { title: 'Last Day',  dataIndex: 'LastDate',  key: 'LastDate',  width: 110 },
    { title: 'AWS ID', dataIndex: 'AWSID', key: 'AWSID', ellipsis: true,
      render: (v) => <span style={{ fontSize: 11, color: '#8c8c8c' }}>{v}</span> },
  ];

  const hrs = (v) => v == null ? <span style={{ color: '#d9d9d9' }}>—</span> : Number(v).toFixed(2);
  const pct = (v) => v == null ? <span style={{ color: '#d9d9d9' }}>—</span> : `${Number(v).toFixed(1)}%`;

  const loadMapping = useCallback(() => {
    if (view !== 'aws-ids') return;
    setMappingLoading(true);
    Promise.all([
      api.get('/api/admin/cne-team-users'),
      api.get('/api/admin/cne-daily/unmapped').catch(() => ({ data: [] })),
    ])
      .then(([usersRes, unmappedRes]) => {
        setTeamUsers(usersRes.data);
        setUnmapped(unmappedRes.data || []);
        const draft = {};
        (usersRes.data || []).forEach((u) => { draft[u.ID] = u.AWSID || ''; });
        setAwsIdDraft(draft);
      })
      .catch(() => message.error('Failed to load team users.'))
      .finally(() => setMappingLoading(false));
  }, [view]);

  useEffect(() => { loadMapping(); }, [loadMapping]);

  const saveAwsId = async (userId) => {
    setSavingIds((prev) => new Set([...prev, userId]));
    try {
      await api.put('/api/admin/cne-aws-id', { user_id: userId, aws_id: awsIdDraft[userId] || null });
      message.success('AWS ID updated.');
      loadMapping();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update AWS ID.');
    } finally {
      setSavingIds((prev) => { const s = new Set(prev); s.delete(userId); return s; });
    }
  };

  const userCols = [
    { title: 'Name', key: 'name', width: 240,
      render: (_, r) => (
        <span>
          {r.DisplayName}
          {!r.IsActive && <Tag color="default" style={{ marginLeft: 8 }}>Inactive</Tag>}
        </span>
      ),
    },
    { title: 'AWS Agent ID', key: 'awsid',
      render: (_, r) => (
        <Input
          size="small"
          value={awsIdDraft[r.ID] ?? ''}
          placeholder="e.g. TSmith@0054N000005Dbc3@00D410000006xvV"
          style={{ fontFamily: 'monospace', fontSize: 12 }}
          onChange={(e) => setAwsIdDraft((prev) => ({ ...prev, [r.ID]: e.target.value }))}
          onPressEnter={() => saveAwsId(r.ID)}
          allowClear
        />
      ),
    },
    { title: '', key: 'save', width: 80,
      render: (_, r) => {
        const changed = (awsIdDraft[r.ID] ?? '') !== (r.AWSID || '');
        return (
          <Button
            size="small"
            type={changed ? 'primary' : 'default'}
            loading={savingIds.has(r.ID)}
            disabled={!changed}
            onClick={() => saveAwsId(r.ID)}
          >
            Save
          </Button>
        );
      },
    },
  ];

  // Below every hook, not above them. An early return here skipped the two
  // hooks defined after it, so an unauthorised render registered fewer hooks
  // than an authorised one -- and React throws the moment isAuthorized
  // changes between renders.
  if (!isAuthorized) {
    return <div style={{ padding: 24 }}><Text type="secondary">Access denied.</Text></div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: view === 'browse' ? '100%' : 1100 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16, gap: 16, flexWrap: 'wrap',
      }}>
        <Title level={4} style={{ margin: 0 }}>CNE Daily Stats</Title>
        <Segmented
          value={view}
          onChange={(v) => { setView(v); setError(null); }}
          options={[
            { label: 'Import',               value: 'upload',  icon: <CloudUploadOutlined /> },
            { label: 'View Uploaded',        value: 'browse',  icon: <TableOutlined /> },
            { label: 'Update AWS Agent IDs', value: 'aws-ids', icon: <IdcardOutlined /> },
          ]}
        />
      </div>

      {view === 'upload' && (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Upload the Historical Metrics Report (one row per agent per day). The export
            carries no campaign column, so each agent is defaulted from their job and shown
            for confirmation before anything is saved. Re-uploading replaces only the agents
            and dates the file contains.
          </Text>

          {!preview && (
            <>
              <Dragger
                accept=".tsv,.txt,.csv"
                beforeUpload={(f) => { setFile(f); reset(); return false; }}
                fileList={file ? [file] : []}
                onRemove={() => { setFile(null); reset(); }}
                maxCount={1}
                style={{ marginBottom: 16 }}
              >
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">Click or drag the export here</p>
                <p className="ant-upload-hint">Tab-separated Historical Metrics Report (.tsv or .txt)</p>
              </Dragger>
              <Button type="primary" icon={<UploadOutlined />} loading={loading}
                      onClick={handlePreview} disabled={!file}>
                Read File
              </Button>
            </>
          )}

          {error && <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />}

          {result && (
            <Alert
              type="success" showIcon style={{ marginTop: 16 }}
              message="Upload saved"
              description={
                <Descriptions size="small" column={3} style={{ marginTop: 8 }}>
                  <Descriptions.Item label="Rows"><Tag color="green">{result.inserted}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Agents">{result.agents}</Descriptions.Item>
                  <Descriptions.Item label="Dates">{result.date_from} to {result.date_to}</Descriptions.Item>
                  <Descriptions.Item label="Linked to a QAUser">{result.matched}</Descriptions.Item>
                  <Descriptions.Item label="No AWS ID on file">
                    {result.unmatched > 0
                      ? <Tag color="orange">{result.unmatched}</Tag>
                      : 0}
                  </Descriptions.Item>
                </Descriptions>
              }
            />
          )}

          {preview && (
            <>
              <Alert
                type={preview.unmatched?.length ? 'warning' : 'info'}
                showIcon
                style={{ marginBottom: 16 }}
                message={preview.unmatched?.length
                  ? `${preview.unmatched.length} agent(s) have no matching AWS ID in QAUsers`
                  : 'Nothing has been saved yet'}
                description={
                  <>
                    <Descriptions size="small" column={4} style={{ marginTop: 8 }}>
                      <Descriptions.Item label="Rows">{preview.row_count}</Descriptions.Item>
                      <Descriptions.Item label="Agents">{preview.agents.length}</Descriptions.Item>
                      <Descriptions.Item label="Days">{preview.day_count}</Descriptions.Item>
                      <Descriptions.Item label="Dates">{preview.date_from} to {preview.date_to}</Descriptions.Item>
                    </Descriptions>
                    {preview.unmatched?.length > 0 && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Their rows still save in full. Rows join to a person on AWS ID at read
                        time, so adding the AWS ID to the QAUser links their history straight
                        away -- no re-upload needed.
                      </Text>
                    )}
                  </>
                }
              />

              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Confirm the campaign for each agent
                {changedCount > 0 && <Tag color="orange" style={{ marginLeft: 8 }}>{changedCount} changed</Tag>}
              </Text>

              <Table
                rowKey="AWSID"
                dataSource={preview.agents}
                columns={agentColumns}
                size="small"
                pagination={false}
                bordered
                style={{ marginBottom: 16 }}
              />

              <Space>
                <Popconfirm
                  title="Save this upload"
                  description={`${preview.row_count} rows across ${preview.day_count} day(s) will replace anything already stored for these agents in that range.`}
                  onConfirm={handleCommit}
                  okText="Save"
                  cancelText="Cancel"
                >
                  <Button type="primary" icon={<SaveOutlined />} loading={saving}>
                    Save {preview.row_count} Rows
                  </Button>
                </Popconfirm>
                <Button onClick={() => { setPreview(null); setAssignments({}); }}>Cancel</Button>
              </Space>
            </>
          )}
        </>
      )}

      {view === 'browse' && (
        <>
          <Space style={{ marginBottom: 16 }} wrap>
            <Text strong>Dates:</Text>
            <RangePicker value={range} onChange={setRange} allowClear={false} />
            {uploaded.length > 0 && <Text type="secondary">{uploaded.length} rows</Text>}
          </Space>

          {upLoading ? <Spin /> : uploaded.length === 0 ? (
            <Alert type="info" showIcon message="Nothing uploaded for this date range." />
          ) : (
            <Table
              rowKey={(r) => `${r.AWSID}-${r.IntervalDate}-${r.CampaignID}`}
              dataSource={uploaded}
              size="small"
              bordered
              pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
              scroll={{ x: 'max-content' }}
              columns={[
                { title: 'Date', dataIndex: 'IntervalDate', key: 'IntervalDate', width: 110,
                  sorter: (a, b) => (a.IntervalDate || '').localeCompare(b.IntervalDate || ''),
                  render: (v) => String(v).substring(0, 10) },
                { title: 'Agent', dataIndex: 'DisplayName', key: 'DisplayName', width: 200,
                  sorter: (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || ''),
                  render: (v, r) => v || <span style={{ fontSize: 11, color: '#8c8c8c' }}>{r.AWSID}</span> },
                { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', width: 140,
                  sorter: (a, b) => (a.CampaignName || '').localeCompare(b.CampaignName || '') },
                { title: 'Handled',  dataIndex: 'ContactsHandled',         key: 'ContactsHandled',  width: 90,  align: 'right',
                  sorter: (a, b) => (a.ContactsHandled ?? 0) - (b.ContactsHandled ?? 0) },
                { title: 'Inbound',  dataIndex: 'ContactsHandledIncoming', key: 'in',  width: 90,  align: 'right' },
                { title: 'Outbound', dataIndex: 'ContactsHandledOutbound', key: 'out', width: 95,  align: 'right' },
                { title: 'Online Hrs',     dataIndex: 'OnlineTimeHrs',    key: 'on',   width: 100, align: 'right', render: hrs },
                { title: 'On Contact Hrs', dataIndex: 'OnContactTimeHrs', key: 'oc',   width: 120, align: 'right', render: hrs },
                { title: 'Occupancy',      dataIndex: 'OccupancyPct',     key: 'occ',  width: 100, align: 'right', render: pct },
                { title: 'Avg Handle Hrs', dataIndex: 'AvgHandleHrs',     key: 'aht',  width: 120, align: 'right', render: hrs },
                { title: 'Talk %',         dataIndex: 'TalkTimePct',      key: 'talk', width: 90,  align: 'right', render: pct },
              ]}
            />
          )}
        </>
      )}

      {/* --- AWS ID MAPPING --------------------------------------------- */}
      {view === 'aws-ids' && (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Set the <strong>AWS Agent ID</strong> for each CNE team member. This is the same field the
            other CNE uploads use, so setting it here fixes all of them. Nothing needs re-uploading
            afterwards: stored rows carry the id rather than a user, and are matched at read time.
          </Text>

          {unmapped.length > 0 && (
            <Alert
              type="warning"
              style={{ marginBottom: 16 }}
              message={`${unmapped.length} AWS ID(s) in stored daily rows belong to nobody yet`}
              description={
                <Table
                  size="small"
                  rowKey="AWSID"
                  dataSource={unmapped}
                  pagination={false}
                  style={{ marginTop: 8 }}
                  columns={[
                    { title: 'AWS Agent ID', dataIndex: 'AWSID', key: 'AWSID',
                      render: (v) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> },
                    { title: 'Dates', key: 'dates', width: 200,
                      render: (_, r) => r.FirstDate === r.LastDate ? r.FirstDate : `${r.FirstDate} to ${r.LastDate}` },
                    { title: 'Rows', dataIndex: 'Rows', key: 'Rows', width: 80, align: 'right' },
                  ]}
                />
              }
            />
          )}

          {mappingLoading
            ? <Spin />
            : (
              <Table
                rowKey="ID"
                dataSource={teamUsers}
                columns={userCols}
                size="small"
                pagination={false}
                bordered
                rowClassName={(r) => !r.IsActive ? 'ant-table-row-muted' : ''}
              />
            )
          }
        </>
      )}
    </div>
  );
}
